import React, { useState } from 'react';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useToast } from '../../contexts/ToastContext';
import { Crown, Check, BookOpen, Bot, Sparkles, Clock } from 'lucide-react';

interface SubscriptionSectionProps {
    onNavigate?: (section: string) => void;
}

const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({ onNavigate }) => {
    const { user, userDoc } = useEntitlements();
    const { showError } = useToast();
    const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

    const handlePortal = async () => {
        if (!user) return;
        setCheckoutLoading('portal');
        try {
            const token = await user.getIdToken();
            const response = await fetch('/.netlify/functions/create-portal-session', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                throw new Error(data.error || 'Failed to create portal session');
            }
        } catch (error) {
            console.error('Portal error:', error);
            showError('Chyba', 'Nepodařilo se otevřít správu předplatného.');
            setCheckoutLoading(null);
        }
    };

    // Tier detection — only count as Ultimate if subscription is actually active
    // (ospAiProActiveUntil must not be expired, and entitlements.pro.status must not be 'expired')
    const ospExpired = userDoc?.ospAiProActiveUntil && userDoc.ospAiProActiveUntil.toDate() <= new Date();
    const isUltimate = !ospExpired && (
        userDoc?.ospSubscriptionType === 'ultimate'
        || userDoc?.ospSubscriptionType === 'trial'
        || (userDoc?.entitlements?.pro?.productId === 'ultimate' && userDoc?.entitlements?.pro?.status === 'active')
    );
    const isLegacy = userDoc?.role === 'plus' ||
        !!userDoc?.prePremiumRole ||
        (userDoc?.role === 'pro' && userDoc?.entitlements?.pro?.status === 'active' && userDoc?.entitlements?.pro?.productId !== 'ultimate');
    const isPastDue = userDoc?.ospSubscriptionStatus === 'past_due' || userDoc?.ospSubscriptionStatus === 'unpaid';
    const isTrial = userDoc?.ospSubscriptionType === 'trial';
    const activeUntil = userDoc?.ospAiProActiveUntil?.toDate();
    const activeUntilFormatted = activeUntil
        ? activeUntil.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })
        : null;

    return (
        <div className="space-y-6">
            {isUltimate && (
                /* ===== PREMIUM / ULTIMATE VIEW ===== */
                <div className={`rounded-2xl p-8 text-white relative overflow-hidden shadow-2xl ${isPastDue ? 'bg-gradient-to-br from-red-900 to-rose-900 ring-2 ring-red-500' : 'bg-gradient-to-br from-indigo-900 to-purple-900'}`}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-3">
                                <Crown className={`w-8 h-8 ${isPastDue ? 'text-red-400' : 'text-amber-400'}`} strokeWidth={2.5} />
                                <div>
                                    <h3 className="text-2xl font-bold">{isPastDue ? 'Premium Pozastaveno' : 'Premium'}</h3>
                                    <p className={isPastDue ? 'text-red-200' : 'text-indigo-200'}>
                                        {isPastDue ? 'Platba selhala. Aktualizuj svou platební metodu.' : 'Máš aktivní kompletní přístup ke všemu.'}
                                    </p>
                                </div>
                            </div>
                            <span className={`px-3 py-1 backdrop-blur-md rounded-full text-sm font-bold border ${isPastDue ? 'bg-red-500/20 border-red-400/30 text-red-200' : isTrial ? 'bg-amber-500/20 border-amber-400/30 text-amber-200' : 'bg-white/20 border-white/10'}`}>
                                {isPastDue ? 'NEZAPLACENO' : isTrial ? 'ZKUŠEBNÍ VERZE' : 'AKTIVNÍ'}
                            </span>
                        </div>

                        {activeUntilFormatted && !isPastDue && (
                            <div className={`flex items-center gap-2 mb-6 px-4 py-2.5 rounded-xl text-sm ${isTrial ? 'bg-amber-500/15 border border-amber-400/20' : 'bg-white/10 border border-white/5'}`}>
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span>
                                    {isTrial ? 'Zkušební verze aktivní do: ' : 'Předplatné aktivní do: '}
                                    <strong>{activeUntilFormatted}</strong>
                                </span>
                            </div>
                        )}

                        {isPastDue && (
                            <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-xl text-sm bg-red-500/20 border border-red-400/30 text-red-100 font-medium">
                                <span>
                                    Přístup k Premium funkcím byl dočasně pozastaven, dokud nebude platba uhrazena.
                                </span>
                            </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-white/10 rounded-xl p-4 border border-white/5">
                                <h4 className="font-bold mb-2 flex items-center gap-2">
                                    <BookOpen className="w-5 h-5 text-indigo-300" /> ZSV Moduly
                                </h4>
                                <ul className="space-y-1 text-sm text-indigo-100">
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Všechny kapitoly</li>
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Flashcards a testy</li>
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Audioknihy</li>
                                </ul>
                            </div>
                            <div className="bg-white/10 rounded-xl p-4 border border-white/5">
                                <h4 className="font-bold mb-2 flex items-center gap-2">
                                    <Bot className="w-5 h-5 text-indigo-300" /> OSP AI Tutor
                                </h4>
                                <ul className="space-y-1 text-sm text-indigo-100">
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Neomezené dotazy (50/den)</li>
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Detailní vysvětlení</li>
                                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-400" /> Sokratovské vedení</li>
                                </ul>
                            </div>
                        </div>

                        <button
                            onClick={handlePortal}
                            disabled={checkoutLoading === 'portal'}
                            className={`w-full sm:w-auto px-6 py-3 font-bold rounded-2xl transition-colors disabled:opacity-50 border-b-4 active:border-b-0 active:translate-y-1 ${isPastDue ? 'bg-white text-red-900 hover:bg-red-50 border-b-red-300' : 'bg-white text-indigo-900 hover:bg-indigo-50 border-b-indigo-300'}`}
                        >
                            {checkoutLoading === 'portal' ? 'Načítání...' : isPastDue ? 'Aktualizovat platební metodu' : 'Spravovat předplatné (Fakturace)'}
                        </button>
                    </div>
                </div>
            )}

            {isLegacy && (
                /* ===== LEGACY VIEW (ZSV Plus/Pro) CARD ===== */
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl border-2 border-amber-200 dark:border-amber-800 p-6">
                    <div className="flex items-start gap-4">
                        <div className="bg-amber-100 dark:bg-amber-900/40 rounded-2xl p-3">
                            <Crown className="w-7 h-7 text-amber-600 dark:text-amber-400" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                                <h4 className="text-lg font-bold text-amber-900 dark:text-amber-100">
                                    Doživotní přístup k ZSV
                                </h4>
                                <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 rounded-full text-xs font-bold">
                                    LEGACY
                                </span>
                            </div>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                                Jako legacy uživatel máš přístup ke všem ZSV materiálům navždy.
                            </p>
                            <ul className="space-y-1.5 text-sm text-amber-800 dark:text-amber-200">
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Všechny kapitoly</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Flashcards a testy</li>
                                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-600 dark:text-amber-400" /> Audioknihy</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {isLegacy && !isUltimate && (
                /* ===== UPGRADE CTA FOR OSP TUTOR ===== */
                <div className="bg-gradient-to-br from-primary/5 to-amber-50 dark:from-primary/10 dark:to-amber-900/10 rounded-2xl border-2 border-primary/20 dark:border-primary/30 p-6">
                    <div className="flex items-start gap-4">
                        <div className="bg-primary/10 dark:bg-primary/20 rounded-2xl p-3">
                            <Sparkles className="w-7 h-7 text-primary" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                                Odemkni OSP AI Tutor
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                Přejdi na Premium a získej přístup k AI tutorovi s 50 dotazy denně, detailními vysvětleními a sokratovským vedením.
                            </p>
                            <button
                                onClick={() => { if (onNavigate) onNavigate('upgrade'); }}
                                disabled={!!checkoutLoading}
                                className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-bold rounded-2xl border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                            >
                                Přejít na Premium
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!isUltimate && !isLegacy && (
                /* ===== FREE USER VIEW ===== */
                <div className="bg-gradient-to-br from-primary/10 to-amber-50 dark:from-primary/15 dark:to-amber-900/10 rounded-2xl border-2 border-primary/20 dark:border-primary/30 p-8 text-center">
                    <div className="flex justify-center mb-4">
                        <div className="bg-primary/15 dark:bg-primary/25 rounded-full p-4">
                            <Crown className="w-10 h-10 text-primary" strokeWidth={2.5} />
                        </div>
                    </div>
                    <h3 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                        Přejdi na Premium
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                        Odemkni všechny materiály, AI tutora a další funkce
                    </p>
                    <button
                        onClick={() => { if (onNavigate) onNavigate('upgrade'); }}
                        disabled={!!checkoutLoading}
                        className="px-8 py-3.5 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-bold rounded-2xl border-b-4 border-b-amber-600 active:border-b-0 active:translate-y-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                    >
                        Přejít na Premium
                    </button>
                </div>
            )}
        </div>
    );
};

export default SubscriptionSection;
